import { useEffect, useMemo, useRef, useState } from 'react'
import { getBatchUrls } from '../utils/fileBrowserApi'
import { kindOf } from '../pages/FileBrowser/fileKinds'

const BATCH_LIMIT = 50 // backend batch-urls hard cap
const EXPIRY_MARGIN_S = 120
const ERROR_RETRY_MS = 60_000

// Fetches signed URLs for image/video files so the grid view can render
// real previews. Cached across bucket/prefix changes and view toggles;
// entries are refetched once their signed URL nears expiry.
export default function useThumbnails({ bucket, files, enabled }) {
  const cacheRef = useRef(new Map())
  const inFlightRef = useRef(new Set())
  const [version, setVersion] = useState(0)

  useEffect(() => {
    if (!enabled) return undefined

    const now = Date.now()
    const needed = files.filter(file => {
      if (!['image', 'video'].includes(kindOf(file.name))) return false
      const cacheKey = `${bucket}:${file.key}`
      if (inFlightRef.current.has(cacheKey)) return false
      const entry = cacheRef.current.get(cacheKey)
      return !entry || entry.expiresAt <= now
    })
    if (!needed.length) return undefined

    let cancelled = false
    needed.forEach(file => inFlightRef.current.add(`${bucket}:${file.key}`))

    const fetchAll = async () => {
      for (let i = 0; i < needed.length; i += BATCH_LIMIT) {
        const chunk = needed.slice(i, i + BATCH_LIMIT)
        try {
          const items = await getBatchUrls(chunk.map(file => ({ bucket, key: file.key })))
          items.forEach(item => {
            cacheRef.current.set(`${bucket}:${item.key}`, {
              url: item.url,
              kind: kindOf(item.key),
              expiresAt: item.expiresIn
                ? Date.now() + (item.expiresIn - EXPIRY_MARGIN_S) * 1000
                : Infinity,
            })
          })
        } catch {
          // Thumbnails are non-fatal; negative-cache so tiles fall back to
          // icons without hammering the endpoint.
          chunk.forEach(file => {
            cacheRef.current.set(`${bucket}:${file.key}`, {
              url: null,
              kind: kindOf(file.name),
              expiresAt: Date.now() + ERROR_RETRY_MS,
            })
          })
        } finally {
          chunk.forEach(file => inFlightRef.current.delete(`${bucket}:${file.key}`))
        }
        if (cancelled) return
        setVersion(v => v + 1)
      }
    }
    fetchAll()

    return () => {
      cancelled = true
    }
  }, [bucket, files, enabled, version])

  return useMemo(() => {
    const thumbs = new Map()
    files.forEach(file => {
      const entry = cacheRef.current.get(`${bucket}:${file.key}`)
      // url: null marks a failed fetch so the grid shows an icon, not a
      // loading placeholder.
      if (entry) thumbs.set(file.key, { url: entry.url, kind: entry.kind })
    })
    return thumbs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, files, version])
}
